import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { AppShell } from "@/components/layouts/AppShell";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { Button } from "@/components/ui/button";
import { HeaderActionGroup, HeaderSearchInput } from "@/components/layouts/header-slots";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PhoneInput } from "@/components/ui/phone-input";
import { Users, Plus, ExternalLink, CheckCircle, ArrowLeft } from "lucide-react";
import { LogoLoader } from "@/components/ui/logo-loader";
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import iconDeleteSvg from '@/assets/circle_delete.svg';
import { useAlert } from '@/hooks/use-alert';
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCurrentOrganization } from '@/hooks/use-current-organization';
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { usePermissions } from "@/hooks/use-permissions";
import { AccessDenied } from "@/components/AccessDenied";
import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js';
import { getDefaultCountry, getDefaultCountryAsync } from '@/utils/country-detection';
import { ConicGradientBorder } from '@/components/conic-gradient-border';
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { IconButtonCancel, IconButtonSave } from "@/components/ui/icon-buttons";
import { useDateFormat } from '@/utils/date-formatter';
import { normalizeSalutationCode } from '@/utils/salutation';

const Contractors = () => {
  const { t } = useTranslation(['pages.contractors']);
  const { confirm } = useAlert();
  const { formatDate } = useDateFormat();
  const { hasPermission } = usePermissions();
  const { organizationId, organizationName, hasOrganization } = useCurrentOrganization();
  
  // Check if user is contractor in the selected organization
  const { isContractor } = useOrganizationSelector();
  
  // All hooks must be called before any early returns
  const [globalTeamSearchTerm, setGlobalTeamSearchTerm] = useState("");
  const [teamSortBy, setTeamSortBy] = useState("name");
  const [teamSortColumn, setTeamSortColumn] = useState<string | null>(null);
  const [teamSortDirection, setTeamSortDirection] = useState<"asc" | "desc">("asc");
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<Array<{name: string; positionId: string | null}>>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedProjectImage, setSelectedProjectImage] = useState<string>("");
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [userForm, setUserForm] = useState({
    anrede: "",
    name: "",
    lastName: "",
    positionId: null as string | null,
    role: "contractor",
    street: "",
    houseNumber: "",
    postalCode: "",
    city: "",
    phone: "",
    mobile: "",
    email: "",
    company: "",
    project: "",
    vacationStart: "",
    vacationEnd: "",
    notes: "",
    isExternal: false
  });
  const [defaultCountry, setDefaultCountry] = useState<CountryCode>(getDefaultCountry());
  const { toast } = useToast();

  // Detect country with IP geolocation when add user modal opens
  useEffect(() => {
    if (isAddUserModalOpen) {
      getDefaultCountryAsync().then((detectedCountry) => {
        if (detectedCountry) {
          setDefaultCountry(detectedCountry);
        }
      }).catch(() => {
        // Silently fail - we already have a fallback from getDefaultCountry()
      });
    }
  }, [isAddUserModalOpen]);
  
  // Fetch all contractors from database (for current organization)
  const { data: teamData, isLoading: isLoadingUsers } = useQuery<{
    data: Array<{
      id: string; 
      userId?: string | null;
      anrede?: string;
      name: string; 
      position?: string; // Legacy field - kept for backward compatibility
      positionId?: string | null;
      positionName?: string | null;
      lastName?: string;
      street?: string;
      houseNumber?: string;
      city?: string;
      phone?: string;
      mobile?: string;
      email?: string;
      company?: string;
      project?: string;
      projects?: Array<{ id: string; name: string }>;
      vacationStart?: string | null;
      vacationEnd?: string | null;
      notes?: string;
      profilePhotoUrl?: string | null;
      isExternal?: boolean;
      invitationStatus?: 'none' | 'pending' | 'sent' | 'accepted' | 'expired' | null;
    }>;
    limits?: {
      maxTeamMembers: number;
      currentCount: number;
    } | null;
  }>({
    queryKey: ["/api/contractors", organizationId], // Include organizationId to refetch when org changes
    queryFn: async () => {
      const url = organizationId ? `/api/contractors?organizationId=${encodeURIComponent(organizationId)}` : "/api/contractors";
      const response = await apiRequest(url);
      const data = await response.json();
      console.log("🔍 Team-Daten empfangen:", data);
      return data;
    },
    enabled: hasOrganization // Only fetch if user has an organization
  });

  // Fetch all projects for the dropdown
  const { data: projectsResponse } = useQuery({
    queryKey: ["/api/projects"],
    queryFn: async () => {
      const response = await apiRequest("/api/projects");
      return await response.json();
    },
  });

  // Fetch all positions from database
  const { data: positionsResponse } = useQuery({
    queryKey: ["/api/positions"],
    queryFn: async () => {
      const response = await apiRequest("/api/positions");
      return await response.json();
    },
  });

  // Use the React Query client from provider
  const rqClient = useQueryClient();
  const updateContractorsCache = useCallback((updater: (items: any[]) => any[]) => {
    rqClient.setQueryData(['/api/contractors', organizationId], (oldData: any) => {
      if (!oldData) return oldData;
      const prev = Array.isArray(oldData)
        ? oldData
        : (Array.isArray(oldData?.data) ? oldData.data : []);
      const next = updater(prev);
      if (Array.isArray(oldData)) return next;
      return { ...oldData, data: next };
    });
  }, [rqClient, organizationId]);

  const contractorMutationPath = useCallback((teamId: string) => {
    const basePath = `/api/contractors/${teamId}`;
    if (!organizationId) {
      return basePath;
    }

    return `${basePath}?organizationId=${encodeURIComponent(organizationId)}`;
  }, [organizationId]);

  const invalidateContractorQueries = useCallback(async () => {
    await Promise.all([
      rqClient.invalidateQueries({ queryKey: ['/api/contractors', organizationId] }),
      rqClient.invalidateQueries({
        predicate: (query) => {
          const firstKey = query.queryKey[0];
          return typeof firstKey === 'string' && firstKey.startsWith('/api/contractors');
        },
      }),
    ]);
  }, [organizationId, rqClient]);

  // Mutations for team management
  const updateTeamMutation = useMutation({
    mutationFn: async ({ id, teamData }: { id: string; teamData: any }) => {
      const response = await apiRequest(contractorMutationPath(id), {
        method: 'PUT',
        body: JSON.stringify(teamData),
      });
      return response.json();
    },
    onSuccess: async (updated) => {
      console.log("🔄 Update mutation success - refreshing team data...");
      updateContractorsCache((prev) => {
        const updatedItem = updated?.data || updated;
        return prev.map((u: any) => (u.id === updatedItem?.id ? { ...u, ...updatedItem } : u));
      });
      await invalidateContractorQueries();
      console.log("✅ Team data refreshed after update");
    },
    onError: (error: any) => {
      toast({
        title: t('pages.contractors:errors.title') || 'Error',
        description: error?.message || t('pages.contractors:messages.error'),
        variant: 'destructive',
      });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(contractorMutationPath(id), {
        method: 'DELETE',
      });
      // Handle empty response (DELETE often returns 204 No Content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return { success: true };
      }
      return response.json();
    },
    onSuccess: (_res, id) => {
      console.log("🔄 Delete mutation success - updating cache optimistically...");
      // Optimistic update: immediately remove the deleted item from cache
      updateContractorsCache((prev) => {
        return prev.filter((u: any) => u.id !== id);
      });
      void invalidateContractorQueries();
      console.log("✅ Team data updated optimistically - no refetch needed");
    },
    onError: (error: any) => {
      toast({
        title: t('pages.contractors:errors.title') || 'Error',
        description: error?.message || t('pages.contractors:messages.error'),
        variant: 'destructive',
      });
    },
  });

  // Mutation for sending invitation email (re-invite)
  const sendInvitationMutation = useMutation({
    mutationFn: async ({ email, organizationId, role }: { email: string; organizationId: string; role?: string }) => {
      const response = await apiRequest('/api/contractors/send-invite', {
        method: 'POST',
        body: JSON.stringify({ email, organizationId, role }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to send invitation';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      return await response.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ['/api/contractors', organizationId] });
      toast({
        title: 'Invitation Sent',
        description: 'The invitation has been sent successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    },
  });

  // Mutation for creating invitation and sending it (first invite)
  const createAndSendInvitationMutation = useMutation({
    mutationFn: async ({ email, organizationId, firstName, lastName, role }: { email: string; organizationId: string; firstName: string; lastName: string; role?: string }) => {
      // First, create the invitation
      const createResponse = await apiRequest('/api/contractors/invite', {
        method: 'POST',
        body: JSON.stringify({ email, organizationId, firstName, lastName, role }),
      });
      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        let errorMessage = 'Failed to create invitation';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Then, send the invitation email
      const sendResponse = await apiRequest('/api/contractors/send-invite', {
        method: 'POST',
        body: JSON.stringify({ email, organizationId, role }),
      });
      if (!sendResponse.ok) {
        const errorText = await sendResponse.text();
        let errorMessage = 'Failed to send invitation email';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      return await sendResponse.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ['/api/contractors', organizationId] });
      toast({
        title: 'Invitation Sent',
        description: 'The invitation has been created and sent successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    },
  });

  // Add contractor to list without sending invitation email (used by Add Contractor modal)
  // The user can send the invitation later by clicking the "Invite" button in the table
  const addContractorMutation = useMutation({
    mutationFn: async (data: any) => {
      const createResponse = await apiRequest('/api/contractors/invite', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        let errorMessage = 'Failed to add member';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      return await createResponse.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ['/api/contractors', organizationId] });
      toast({
        title: t('pages.contractors:contractorAdded') || 'Member Added',
        description: t('pages.contractors:contractorAddedDescription') || 'You can send the invitation from the list.',
      });
      setIsAddUserModalOpen(false);
      resetInviteForm();
    },
    onError: (error: any) => {
      let errorMessage = error.message || t('errors.addContractorFailed');
      if (errorMessage.includes('mobile') || errorMessage.includes('phone') || errorMessage.includes('Invalid phone number')) {
        errorMessage = t('errors.invalidMobile');
      }
      toast({
        title: t('errors.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
  
  // Block contractors from accessing team page (after all hooks)
  if (isContractor) {
    return (
      <AppShell>
        <div className="flex-1 overflow-auto bg-white p-4">
          <AccessDenied permission="manage_members" />
        </div>
      </AppShell>
    );
  }
  
  // Check permission
  if (!hasPermission('manage_members')) {
    return (
      <AppShell>
        <div className="flex-1 overflow-auto bg-white p-4">
          <AccessDenied permission="manage_members" />
        </div>
      </AppShell>
    );
  }
  
  // Tabellenformel: Enhanced dynamic column width calculation
  const calculateColumnWidths = (users: any[]) => {
    const minColumnWidth = 80; // Mindestbreite pro Spalte
    const maxColumnWidth = 250; // Maximale Spaltenbreite
    const actionColumnWidth = 100; // Feste Breite für Aktionen
    const selectColumnWidth = 48; // Fixed width for row selection
    const totalPadding = 32; // Gesamter Padding für Container
    
    // Verfügbare Gesamtbreite berechnen
    const availableWidth = Math.max(window.innerWidth - 280, 800); // Mindestens 800px
    const statusColumnWidth = 100; // Fixed width for status column
    const contentWidth = availableWidth - actionColumnWidth - statusColumnWidth - selectColumnWidth - totalPadding;
    
    // Spalten-Inhalte analysieren
    const columnData = {
      name: users.map(u => u?.name || ''),
      lastName: users.map(u => u?.lastName || ''),
      mobile: users.map(u => getUserMobileDisplay(u)),
      email: users.map(u => u?.email || ''),
      role: users.map(u => u?.role || ''),
      position: users.map(u => u?.position || ''),
      project: users.map(u => u?.project || '')
    };
    
    // Präzise Textbreiten-Berechnung (8.5px pro Zeichen für bessere Genauigkeit)
    const getOptimalWidth = (values: string[], headerText: string) => {
      const maxTextLength = Math.max(
        headerText.length,
        ...values.map(v => v.length)
      );
      const calculatedWidth = Math.max(maxTextLength * 8.5 + 20, minColumnWidth);
      return Math.min(calculatedWidth, maxColumnWidth);
    };
    
    // Ideale Breiten berechnen
    const idealWidths = {
      name: getOptimalWidth(columnData.name, 'Name'),
      lastName: getOptimalWidth(columnData.lastName, 'Nachname'),
      mobile: getOptimalWidth(columnData.mobile, 'Mobiltelefon'),
      email: getOptimalWidth(columnData.email, 'E-Mail'),
      role: getOptimalWidth(columnData.role, 'Role'),
      position: getOptimalWidth(columnData.position, 'Position'),
      project: getOptimalWidth(columnData.project, 'Projekt')
    };
    
    const totalIdealWidth = Object.values(idealWidths).reduce((sum, width) => sum + width, 0);
    
    // Anpassung wenn Platz übrig oder zu wenig ist
    let finalWidths;
    if (totalIdealWidth <= contentWidth) {
      // Mehr Platz verfügbar - proportional verteilen
      const scaleFactor = contentWidth / totalIdealWidth;
      finalWidths = {
        name: idealWidths.name * scaleFactor,
        lastName: idealWidths.lastName * scaleFactor,
        mobile: idealWidths.mobile * scaleFactor,
        email: idealWidths.email * scaleFactor,
        role: idealWidths.role * scaleFactor,
        position: idealWidths.position * scaleFactor,
        project: idealWidths.project * scaleFactor
      };
    } else {
      // Zu wenig Platz - proportional reduzieren
      const reductionFactor = contentWidth / totalIdealWidth;
      finalWidths = {
        name: Math.max(idealWidths.name * reductionFactor, minColumnWidth),
        lastName: Math.max(idealWidths.lastName * reductionFactor, minColumnWidth),
        mobile: Math.max(idealWidths.mobile * reductionFactor, minColumnWidth),
        email: Math.max(idealWidths.email * reductionFactor, minColumnWidth),
        role: Math.max(idealWidths.role * reductionFactor, minColumnWidth),
        position: Math.max(idealWidths.position * reductionFactor, minColumnWidth),
        project: Math.max(idealWidths.project * reductionFactor, minColumnWidth)
      };
    }
    
    // Prozentuale Umrechnung für CSS
    return {
      select: `${Math.round((selectColumnWidth / availableWidth) * 100)}%`,
      name: `${Math.round((finalWidths.name / availableWidth) * 100)}%`,
      lastName: `${Math.round((finalWidths.lastName / availableWidth) * 100)}%`,
      mobile: `${Math.round((finalWidths.mobile / availableWidth) * 100)}%`,
      email: `${Math.round((finalWidths.email / availableWidth) * 100)}%`,
      role: `${Math.round((finalWidths.role / availableWidth) * 100)}%`,
      position: `${Math.round((finalWidths.position / availableWidth) * 100)}%`,
      project: `${Math.round((finalWidths.project / availableWidth) * 100)}%`,
      status: `${Math.round((statusColumnWidth / availableWidth) * 100)}%`,
      actions: `${Math.round((actionColumnWidth / availableWidth) * 100)}%`
    };
  };

  // Minimal validation for creating/updating a member
  // Only validate when creating new members (editingUser is null)
  // When editing, we only update organization-specific fields which don't need validation
  const isUserFormValid = () => {
    // If editing, no validation needed (only updating organization-specific fields)
    if (editingUser) {
      return true;
    }
    // If creating, validate required fields (email, name, surname; mobile is optional for invite)
    const email = (userForm.email || '').trim();
    const name = (userForm.name || '').trim();
    const last = (userForm.lastName || '').trim();
    const mobile = (userForm.mobile || '').trim();
    const emailRegex = /\S+@\S+\.[\S]+/;
    const mobileValid = mobile.length === 0 || isValidPhoneNumber(mobile);
    return name.length > 0 && last.length > 0 && emailRegex.test(email) && mobileValid;
  };

  // Memoize availableUsers to prevent infinite loops
  const availableUsers = useMemo(() => teamData?.data || [], [teamData?.data]);
  // Contractors are unlimited - no limits to check

  const projects = projectsResponse?.data || [];
  const positions = positionsResponse?.data || [];
  const isRegisteredUser =
    !!editingUser &&
    !!editingUser.userId &&
    !editingUser.isExternal &&
    editingUser.invitationStatus !== 'pending' &&
    editingUser.invitationStatus !== 'sent';
  const positionsById = useMemo(() => {
    const map = new Map<string, string>();
    positions.forEach((position: any) => {
      if (!position?.id) return;
      const label = position.displayName || position.name || '';
      if (label) map.set(position.id, label);
    });
    return map;
  }, [positions]);

  const getUserPositionLabel = (user: any): string => {
    // Prefer translated position name from positionsById (uses current language)
    if (user?.positionId) {
      const translated = positionsById.get(user.positionId);
      if (translated) return translated;
    }
    // Fallback to raw position name from API (untranslated)
    return user?.positionName || user?.position || '';
  };

  const getUserMobileDisplay = (user: any): string => {
    const mobile = (user?.mobile || '').trim();
    if (mobile) return mobile;
    const phone = (user?.phone || '').trim();
    return phone;
  };

  // Helper function to reset invite form
  const resetInviteForm = () => {
    setUserForm({
      anrede: "",
      name: "",
      lastName: "",
      positionId: null,
      role: "contractor",
      street: "",
      houseNumber: "",
      postalCode: "",
      city: "",
      phone: "",
      mobile: "",
      email: "",
      company: "",
      project: "",
      vacationStart: "",
      vacationEnd: "",
      notes: "",
      isExternal: false
    });
    setSelectedProjectIds([]);
    setSelectedProjectImage("");
  };

  // For now, we'll use all available users as selected users
  // In a real app, you might want to have a separate "selected team" concept
  const selectedUsers = availableUsers;

  const getUserSelectionId = (user: any) => user.id || user.email;

  // Use a stable dependency - only update when the actual user IDs change
  const userIdsString = useMemo(() => 
    availableUsers.map(getUserSelectionId).join(','), 
    [availableUsers]
  );

  useEffect(() => {
    // Reconstruct IDs from the stable string to avoid stale closures
    const currentIds = new Set(userIdsString.split(',').filter(Boolean));
    setSelectedUserIds((prev) => {
      const filtered = prev.filter((id) => currentIds.has(id));
      // Only update if the filtered array is different
      if (filtered.length !== prev.length || filtered.some((id, idx) => id !== prev[idx])) {
        return filtered;
      }
      return prev;
    });
  }, [userIdsString]);

  const filteredUsers = selectedUsers.filter(user => {
    if (!user) return false;
    const searchTerm = globalTeamSearchTerm.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(searchTerm) ||
      (user.lastName || '').toLowerCase().includes(searchTerm) ||
      (getUserPositionLabel(user).toLowerCase().includes(searchTerm)) ||
      (user.anrede || '').toLowerCase().includes(searchTerm) ||
      (user.street || '').toLowerCase().includes(searchTerm) ||
      (user.houseNumber || '').toLowerCase().includes(searchTerm) ||
      (user.city || '').toLowerCase().includes(searchTerm) ||
      (user.phone || '').toLowerCase().includes(searchTerm) ||
      (getUserMobileDisplay(user).toLowerCase().includes(searchTerm)) ||
      (user.email || '').toLowerCase().includes(searchTerm) ||
      (user.project || '').toLowerCase().includes(searchTerm) ||
      (user.notes || '').toLowerCase().includes(searchTerm)
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (teamSortColumn) {
      const aValue = teamSortColumn === "lastName" ? (a.lastName || "") : 
                   teamSortColumn === "mobile" ? getUserMobileDisplay(a) :
                   teamSortColumn === "email" ? (a.email || "") :
                   teamSortColumn === "role" ? ((a as { role?: string }).role || "") :
                   teamSortColumn === "position" ? getUserPositionLabel(a) :
                   teamSortColumn === "project" ? (a.project || "") :
                   (a.name || "");
      
      const bValue = teamSortColumn === "lastName" ? (b.lastName || "") : 
                   teamSortColumn === "mobile" ? getUserMobileDisplay(b) :
                   teamSortColumn === "email" ? (b.email || "") :
                   teamSortColumn === "role" ? ((b as { role?: string }).role || "") :
                   teamSortColumn === "position" ? getUserPositionLabel(b) :
                   teamSortColumn === "project" ? (b.project || "") :
                   (b.name || "");
      
      const result = aValue.localeCompare(bValue);
      return teamSortDirection === "asc" ? result : -result;
    } else if (teamSortBy === "name") {
      return (a.name || "").localeCompare(b.name || "");
    } else {
      return getUserPositionLabel(a).localeCompare(getUserPositionLabel(b));
    }
  });

  const dynamicWidths = calculateColumnWidths(sortedUsers);
  const allSelected = sortedUsers.length > 0 && sortedUsers.every((user) => selectedUserIds.includes(getUserSelectionId(user)));
  const someSelected = selectedUserIds.length > 0 && !allSelected;

  const handleBulkDeleteUsers = async () => {
    if (selectedUserIds.length === 0) return;
    const confirmed = await confirm({
      title: t('modal.delete.title'),
      message: t('modal.delete.descriptionMultiple', { count: selectedUserIds.length }),
      variant: 'destructive'
    });
    if (!confirmed) return;

    for (const userId of selectedUserIds) {
      await deleteTeamMutation.mutateAsync(userId);
    }
    setSelectedUserIds([]);
  };

  const handleAddUsers = () => {
    if (selectedUsersToAdd.length > 0) {
      // Add logic here to handle adding users to a specific team or project
      console.log("Adding users:", selectedUsersToAdd);
      setSelectedUsersToAdd([]);
      setIsAddUserModalOpen(false);
    }
  };

  const handleInviteContractor = async (user: { email: string; name?: string; lastName?: string; role?: string; invitationStatus?: 'none' | 'pending' | 'sent' | 'accepted' | 'expired' | null }) => {
    if (!organizationId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive"
      });
      return;
    }
    
    if (!user.email) {
      toast({
        title: "Error",
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }

    const confirmed = await confirm({
      title: t('inviteConfirmTitle'),
      message: t('inviteConfirmMessage', { email: user.email }),
      confirmText: t('common:confirm'),
      cancelText: t('common:cancel'),
    });

    if (!confirmed) return;

    // If invitation status is 'sent', use re-invite endpoint (only needs email)
    if (user.invitationStatus === 'sent') {
      console.log('🔄 Re-inviting contractor:', user.email);
      sendInvitationMutation.mutate({ 
        email: user.email, 
        organizationId,
        role: user.role || 'contractor',
      });
      return;
    }
    
    // For first invite, need name and lastName
    if (!user.email || !user.name || !user.lastName) {
      toast({
        title: "Error",
        description: "User information is incomplete. Please ensure name and last name are available.",
        variant: "destructive"
      });
      return;
    }
    
    // Create invitation and send email
    console.log('📧 Creating and sending invitation for:', user.email);
    createAndSendInvitationMutation.mutate({ 
      email: user.email, 
      organizationId, 
      firstName: user.name, 
      lastName: user.lastName,
      role: user.role || 'contractor',
    });
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    // Use positionId if available, otherwise try to find ID from position name
    let positionId: string | null = user.positionId || null;
    if (!positionId && user.position && positions && positions.length > 0) {
      const matchingPosition = positions.find((p: any) => p.name === user.position);
      positionId = matchingPosition?.id || null;
    }
    
    setUserForm({
      anrede: normalizeSalutationCode(user.anrede) || "",
      name: user.name || "",
      lastName: user.lastName || "",
      positionId: positionId,
      role: user.role || "contractor",
      street: user.street || "",
      houseNumber: user.houseNumber || "",
      postalCode: user.postalCode || "",
      city: user.city || "",
      phone: user.phone || "",
      mobile: getUserMobileDisplay(user),
      email: user.email || "",
      company: user.company || "",
      project: user.project || "",
      vacationStart: user.vacationStart || "",
      vacationEnd: user.vacationEnd || "",
      notes: user.notes || "",
      isExternal: user.isExternal || false
    });
    // Set selected project IDs from user.projects array
    if (user.projects && Array.isArray(user.projects) && user.projects.length > 0) {
      setSelectedProjectIds(user.projects.map((p: any) => p.id || p));
      // Set project image from first project if available
      const firstProject = projects.find(p => p.id === (user.projects[0].id || user.projects[0]));
      setSelectedProjectImage(firstProject?.imageUrl || "");
    } else {
      setSelectedProjectIds([]);
      setSelectedProjectImage("");
    }
    setIsAddUserModalOpen(true);
  };

  const handleAddNewUser = () => {
    if (!hasOrganization) {
      toast({
        title: "Error",
        description: "You must be part of an organization to add members.",
        variant: "destructive"
      });
      return;
    }
    
    setEditingUser(null);
    setUserForm({
      anrede: "",
      name: "",
      lastName: "",
      positionId: null,
      role: "contractor",
      street: "",
      houseNumber: "",
      postalCode: "",
      city: "",
      phone: "",
      mobile: "",
      email: "",
      company: "",
      project: "",
      vacationStart: "",
      vacationEnd: "",
      notes: "",
      isExternal: false
    });
    setSelectedProjectImage("");
    setIsAddUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    // Verhindere Doppelklicks während der Speicherung
    if (isSaving) return;
    if (!isUserFormValid()) {
      toast({
        title: t('pages.contractors:validation.invalidTitle') || 'Ungültige Eingaben',
        description: t('pages.contractors:validation.minimal') || 'Bitte Name, Nachname und eine gültige E-Mail angeben.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingUser) {
        const isPendingInvitation =
          !editingUser.userId &&
          (editingUser.invitationStatus === 'pending' ||
            editingUser.invitationStatus === 'sent');
        const isLegacyExternalContractor =
          !!editingUser.isExternal && !isPendingInvitation;
        
        if (isPendingInvitation || isLegacyExternalContractor) {
          // Pending invitations and legacy external contractors own their personal fields here.
          const teamData = {
            firstName: userForm.name,
            lastName: userForm.lastName,
            email: userForm.email,
            role: userForm.role || 'contractor',
            anrede: normalizeSalutationCode(userForm.anrede),
            mobile: userForm.mobile || null,
            positionId: userForm.positionId || null,
            projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : null,
            vacationStart: userForm.vacationStart || null,
            vacationEnd: userForm.vacationEnd || null,
            notes: userForm.notes || null,
          };
          await updateTeamMutation.mutateAsync({ id: editingUser.id, teamData });
          console.log("✅ Pending invitation aktualisiert:", userForm.name);
        } else {
          // Update existing registered contractor - send only org-specific fields (not personal)
          const teamData = {
            positionId: userForm.positionId || null,
            anrede: normalizeSalutationCode(userForm.anrede),
            role: userForm.role || 'contractor',
            projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : null,
            vacationStart: userForm.vacationStart || null,
            vacationEnd: userForm.vacationEnd || null,
            notes: userForm.notes || null,
          };
          await updateTeamMutation.mutateAsync({ id: editingUser.id, teamData });
          console.log("✅ Team-Mitglied aktualisiert:", userForm.name);
        }
      } else {
        // Create new contractor and send invitation with full form data
        if (!organizationId) throw new Error('No organization selected');
        if (!userForm.email || !userForm.name || !userForm.lastName) {
          throw new Error('Email, name, and surname are required');
        }
        await addContractorMutation.mutateAsync({
          email: userForm.email,
          organizationId,
          firstName: userForm.name,
          lastName: userForm.lastName,
          positionId: userForm.positionId || undefined,
          anrede: normalizeSalutationCode(userForm.anrede) || undefined,
          mobile: userForm.mobile || undefined,
          projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
          vacationStart: userForm.vacationStart || undefined,
          vacationEnd: userForm.vacationEnd || undefined,
          notes: userForm.notes || undefined,
          role: userForm.role || 'contractor',
        });
        console.log("✅ Contractor added to list:", userForm.email);
      }
      
      // Close modal and reset
      setIsAddUserModalOpen(false);
      setEditingUser(null);
      setUserForm({
        anrede: "",
        name: "",
        lastName: "",
        positionId: null,
        role: "contractor",
        street: "",
        houseNumber: "",
        postalCode: "",
        city: "",
        phone: "",
        mobile: "",
        email: "",
        company: "",
        project: "",
        vacationStart: "",
        vacationEnd: "",
        notes: "",
        isExternal: false
      });
      setSelectedProjectIds([]);
    } catch (error) {
      console.error('❌ Fehler beim Speichern des Team-Mitglieds:', error);
      toast({
        title: t('pages.contractors:errors.title') || 'Error',
        description:
          error instanceof Error
            ? error.message
            : t('pages.contractors:messages.error'),
        variant: 'destructive',
      });
    } finally {
      // Loading-State immer zurücksetzen
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsAddUserModalOpen(false);
    setEditingUser(null);
    setUserForm({
      anrede: "",
      name: "",
      lastName: "",
      positionId: null,
      role: "contractor",
      street: "",
      houseNumber: "",
      postalCode: "",
      city: "",
      phone: "",
      mobile: "",
      email: "",
      company: "",
      project: "",
      vacationStart: "",
      vacationEnd: "",
      notes: "",
      isExternal: false
    });
    setSelectedProjectIds([]);
  };

  const handleUpdateUserField = async (userId: string, field: 'positionId' | 'project' | 'vacationStart' | 'vacationEnd' | 'notes', value: string) => {
    try {
      const userToUpdate = selectedUsers.find(u => u.id === userId);
      if (!userToUpdate) return;

      // Only send organization-specific editable fields
      const updatedUser: any = {
        project: (userToUpdate as any).project || null,
        vacationStart: (userToUpdate as any).vacationStart || null,
        vacationEnd: (userToUpdate as any).vacationEnd || null,
        notes: (userToUpdate as any).notes || null,
        [field]: value  // Das geänderte Feld überschreiben
      };
      // Send positionId if field is positionId, otherwise send positionId from userToUpdate
      if (field === 'positionId') {
        updatedUser.positionId = value;
      } else if (userToUpdate.positionId) {
        updatedUser.positionId = userToUpdate.positionId;
      }

      await apiRequest(contractorMutationPath(userId), {
        method: 'PUT',
        body: JSON.stringify(updatedUser),
      });
      
      console.log(`✅ Team-Mitglied Feld ${field} aktualisiert:`, value);
      // OPTIMISTIC FIELD UPDATE: Sofort im Frontend aktualisieren
      const selectedPosition = field === 'positionId'
        ? positions.find((position: any) => position.id === value)
        : null;
      const positionLabel = selectedPosition
        ? (selectedPosition.displayName || selectedPosition.name || null)
        : null;
      updateContractorsCache((dataArray) => dataArray.map((user: any) =>
        user.id === userId
          ? {
              ...user,
              [field]: value,
              ...(field === 'positionId'
                ? {
                    positionId: value || null,
                    positionName: positionLabel,
                    position: positionLabel,
                  }
                : {}),
            }
          : user
      ));
      await invalidateContractorQueries();
      console.log("✅ Feld sofort aktualisiert:", field, "=", value);
    } catch (error) {
      console.error('❌ Fehler beim Aktualisieren des Team-Mitglieds:', error);
      toast({
        title: t('pages.contractors:errors.title') || 'Error',
        description:
          error instanceof Error
            ? error.message
            : t('pages.contractors:messages.error'),
        variant: 'destructive',
      });
    }
  };

  return (
    <AppShell>
      <PageHeaderLayout
        pageTitle={t('title')}
        pageSubtitle={`${selectedUsers.length} ${t('memberCount')}`}
        searchComponent={
          <HeaderSearchInput
            value={globalTeamSearchTerm}
            onChange={setGlobalTeamSearchTerm}
            placeholder={t('search')}
          />
        }
        actionButtons={
          <HeaderActionGroup>
            <Button
              variant="pillOutline"
              onClick={handleAddNewUser}
              size="sm"
              className="bg-white hover:bg-white"
              disabled={!hasOrganization}
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('addContractor') || t('addMember')}
            </Button>
          </HeaderActionGroup>
        }
      >
        <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
          <div className="flex gap-4 max-h-full min-h-0">
            <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
              <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 max-h-full">
                <div className="overflow-auto min-h-0 pb-4">
                  {isLoadingUsers ? (
                    <div className="flex items-center justify-center py-16">
                      <LogoLoader size="md" />
                    </div>
                  ) : (<>
                    <table className="min-w-full listen-table">
                      <thead className="sticky top-0 z-10">
                        <tr className="table-header-row">
                          {(() => {
                            return (
                              <>
                          <th className="table-header-cell text-center" style={{width: dynamicWidths.select}}>
                            <Checkbox
                              checked={allSelected ? true : someSelected ? "indeterminate" : false}
                              onCheckedChange={() => {
                                if (allSelected) {
                                  setSelectedUserIds([]);
                                  return;
                                }
                                setSelectedUserIds(sortedUsers.map(getUserSelectionId));
                              }}
                              aria-label={t('table.headers.selectAll')}
                            />
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.name}}>
                            <button 
                              onClick={() => {
                                const newColumn = "name";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.name')}</span>
                              {teamSortColumn === "name" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.lastName}}>
                            <button 
                              onClick={() => {
                                const newColumn = "lastName";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.lastName')}</span>
                              {teamSortColumn === "lastName" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.mobile}}>
                            <button 
                              onClick={() => {
                                const newColumn = "mobile";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.mobile')}</span>
                              {teamSortColumn === "mobile" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.email}}>
                            <button 
                              onClick={() => {
                                const newColumn = "email";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.email')}</span>
                              {teamSortColumn === "email" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.role}}>
                            <button 
                              onClick={() => {
                                const newColumn = "role";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.role')}</span>
                              {teamSortColumn === "role" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.position}}>
                            <button 
                              onClick={() => {
                                const newColumn = "position";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.position')}</span>
                              {teamSortColumn === "position" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.project}}>
                            <button 
                              onClick={() => {
                                const newColumn = "project";
                                const newDirection = teamSortColumn === newColumn && teamSortDirection === "asc" ? "desc" : "asc";
                                setTeamSortColumn(newColumn);
                                setTeamSortDirection(newDirection);
                              }}
                              className="flex items-center gap-1 hover:text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                            >
                              <span className="overflow-hidden text-ellipsis">{t('table.headers.project')}</span>
                              {teamSortColumn === "project" && <span className="text-[10px] flex-shrink-0">{teamSortDirection === "asc" ? "↓" : "↑"}</span>}
                            </button>
                          </th>
                          <th className="table-header-cell" style={{width: dynamicWidths.status}}>
                            <span className="whitespace-nowrap overflow-hidden text-ellipsis block">{t('table.headers.status') || 'Status'}</span>
                          </th>
                          <th className="table-header-cell text-right" style={{width: dynamicWidths.actions}}>
                            <span className="whitespace-nowrap overflow-hidden text-ellipsis block">{t('table.headers.actions')}</span>
                          </th>
                              </>
                            );
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.max(10, sortedUsers.length) }).map((_, index) => {
                          const user = sortedUsers[index];
                          const userId = user ? getUserSelectionId(user) : null;
                          return (
                            <tr 
                              key={index} 
                              className={`table-body-row ${user ? "cursor-pointer" : ""}`}
                              onClick={() => user && handleEditUser(user)}
                            >
                              <td className="table-body-cell text-center" style={{width: dynamicWidths.select}}>
                                {user ? (
                                  <Checkbox
                                    checked={userId ? selectedUserIds.includes(userId) : false}
                                    onCheckedChange={(checked) => {
                                      if (!userId) return;
                                      if (checked) {
                                        setSelectedUserIds((prev) => [...prev, userId]);
                                      } else {
                                        setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
                                      }
                                    }}
                                    onClick={(event) => event.stopPropagation()}
                                    aria-label={t('table.headers.selectRow')}
                                  />
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.name}}>
                                {user ? (
                                  <span>{user.name}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.lastName}}>
                                {user ? (
                                  <span>{user.lastName || ''}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.mobile}}>
                                {user ? (
                                  <span>{getUserMobileDisplay(user)}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.email}}>
                                {user ? (
                                  <span>{user.email || ''}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.role}}>
                                {user ? (
                                  <span>{t(`pages.contractors:form.options.role.${(user as { role?: string }).role || 'contractor'}`)}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.position}}>
                                {user ? (
                                  <span>{getUserPositionLabel(user)}</span>
                                ) : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.project}}>
                                {user ? (() => {
                                  const userProjects = user.projects || [];
                                  const oldProject = user.project;
                                  
                                  if (userProjects.length > 0) {
                                    if (userProjects.length === 1) {
                                      const project = userProjects[0];
                                      return <span>{typeof project === 'object' ? project.name : project}</span>;
                                    } else {
                                      return <span>{userProjects.length}+</span>;
                                    }
                                  }
                                  if (oldProject) {
                                    return <span>{oldProject}</span>;
                                  }
                                  return <span>-</span>;
                                })() : null}
                              </td>
                              <td className="table-body-cell whitespace-nowrap" style={{width: dynamicWidths.status}}>
                                {user ? (
                                  <>
                                    {user.userId ? (
                                      <span>-</span>
                                    ) : user.invitationStatus === 'sent' ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
                                        {t('pages.contractors:invitedStatus') || 'Invited'}
                                      </span>
                                    ) : user.invitationStatus === 'pending' ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-800">
                                        {t('pages.contractors:pendingStatus') || 'Pending'}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-800">
                                        {t('pages.contractors:pendingStatus') || 'Pending'}
                                      </span>
                                    )}
                                  </>
                                ) : null}
                              </td>
                              <td className="table-body-cell text-right whitespace-nowrap" style={{width: dynamicWidths.actions}}>
                                {user ? (
                                  <div className="flex justify-end items-center gap-2">
                                    {!user.userId && (
                                      <>
                                        {(!user.invitationStatus || user.invitationStatus === 'pending' || user.invitationStatus === 'none') && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (user.email && user.name && user.lastName && !createAndSendInvitationMutation.isPending) {
                                                void handleInviteContractor(user as { email: string; name: string; lastName: string; role?: string; invitationStatus?: 'none' | 'pending' | 'sent' | 'accepted' | 'expired' | null });
                                              }
                                            }}
                                            disabled={createAndSendInvitationMutation.isPending || !user.email || !user.name || !user.lastName}
                                            className="list-action-button-black"
                                          >
                                            {createAndSendInvitationMutation.isPending ? '...' : (t('pages.contractors:inviteButton') || 'Invite')}
                                          </button>
                                        )}
                                        {user.invitationStatus === 'sent' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (user.email && !sendInvitationMutation.isPending) {
                                                void handleInviteContractor(user as { email: string; name?: string; lastName?: string; role?: string; invitationStatus?: 'none' | 'pending' | 'sent' | 'accepted' | 'expired' | null });
                                              }
                                            }}
                                            disabled={sendInvitationMutation.isPending || !user.email}
                                            className="list-action-button-black"
                                          >
                                            {sendInvitationMutation.isPending ? '...' : (t('pages.contractors:reinviteButton') || 'Re-invite')}
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  
                  {selectedUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-xs">{t('empty.title')}</p>
                      <p className="text-xs text-slate-400 mt-1">{t('empty.subtitle')}</p>
                    </div>
                  )}
                  </>)}
                </div>
              </div>
            </div>

            {/* Right Sidebar Menu */}
            <RightSidebar>
              <SidebarButtonGroup label={t('modal.buttons.delete')} hideLabel>
                <SidebarIconButton
                  icon={iconDeleteSvg}
                  alt="Delete"
                  onClick={handleBulkDeleteUsers}
                  disabled={selectedUserIds.length === 0}
                  title={t('modal.buttons.delete')}
                />
              </SidebarButtonGroup>
            </RightSidebar>
          </div>
        </div>
        </PageHeaderLayout>

            {/* Add User Modal - Full form matching edit form */}
            <Dialog open={isAddUserModalOpen} onOpenChange={(open) => {
              if (open) {
                setIsAddUserModalOpen(true);
                return;
              }
              handleCancelEdit();
            }}>
              <DialogContent className="ticket-modal-shell [&>button]:hidden" hideClose>
                <div className="ticket-modal-header">
                  <div className="ticket-modal-title-row">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={handleCancelEdit}
                      className="h-8 w-8 p-0 text-[#333333] hover:text-[#333333] hover:bg-gray-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <DialogTitle className="ticket-modal-title">
                      {editingUser
                        ? (t('pages.contractors:modal.edit.title') || 'Edit Member')
                        : (t('pages.contractors:modal.add.title') || 'Add Member')}
                    </DialogTitle>
                  </div>
                  <div className="ticket-modal-actions">
                    <IconButtonCancel
                      onClick={handleCancelEdit}
                      size={30}
                      title={t('pages.contractors:modal.buttons.cancel') || 'Cancel'}
                    />
                    <IconButtonSave
                      onClick={handleSaveUser}
                      disabled={!isUserFormValid() || isSaving || (!hasOrganization && !editingUser)}
                      size={30}
                      title={isSaving
                        ? (t('pages.contractors:modal.buttons.saving') || 'Saving...')
                        : editingUser
                          ? (t('pages.contractors:modal.buttons.save') || 'Save')
                          : (t('pages.contractors:modal.buttons.add') || 'Add Member')}
                    />
                  </div>
                </div>
                <DialogDescription className="sr-only">
                  {t('pages.contractors:modal.add.description') || 'Add a new member to your organization'}
                </DialogDescription>

                <div className="ticket-modal-scroll">
                  <div className="ticket-modal-body">
                    <div className="card-container card-container-muted card-container-no-shadow p-[32px]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - All Fields */}
                  <div className="space-y-4">
                    {isRegisteredUser && (
                      <p className="text-xs text-slate-600 mb-1">
                        {t('pages.contractors:form.personalFieldsHint')}
                      </p>
                    )}
                    {/* Anrede und Name */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <CornerLabelField label={t('pages.contractors:form.fields.salutation')}>
                          <Select
                            value={userForm.anrede || ""}
                            onValueChange={(value) => setUserForm(prev => ({ ...prev, anrede: normalizeSalutationCode(value) || "" }))}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('pages.contractors:form.placeholders.salutation')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MR">{t('pages.contractors:form.options.salutation.mr') || 'Mr.'}</SelectItem>
                              <SelectItem value="MRS">{t('pages.contractors:form.options.salutation.mrs') || 'Mrs.'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </CornerLabelField>
                      </div>
                      <div className="col-span-2">
                        <CornerLabelField label={`${t('pages.contractors:form.fields.name')} ${!isRegisteredUser ? '*' : ''}`}>
                          <Input
                            id="add-name"
                            value={userForm.name || ""}
                            onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('pages.contractors:form.placeholders.name') || 'Peter'}
                            disabled={isRegisteredUser}
                            className={isRegisteredUser ? 'bg-gray-50 text-[#666666] cursor-not-allowed' : undefined}
                          />
                        </CornerLabelField>
                      </div>
                    </div>

                    {/* Nachname und Position */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <CornerLabelField label={`${t('pages.contractors:form.fields.lastName')} ${!isRegisteredUser ? '*' : ''}`}>
                          <Input
                            id="add-lastName"
                            value={userForm.lastName || ""}
                            onChange={(e) => setUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                            placeholder={t('pages.contractors:form.placeholders.lastName') || 'Weber'}
                            disabled={isRegisteredUser}
                            className={isRegisteredUser ? 'bg-gray-50 text-[#666666] cursor-not-allowed' : undefined}
                          />
                        </CornerLabelField>
                      </div>
                      <div>
                        <CornerLabelField label={t('pages.contractors:form.fields.position')}>
                          <Select
                            value={userForm.positionId || ""}
                            onValueChange={(value) => setUserForm(prev => ({ ...prev, positionId: value || null }))}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={
                                  t('pages.contractors:form.placeholders.position') ||
                                  t('pages.contractors:form.placeholder.position')
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {positions.filter((position: any) => position.name && position.name.trim() !== "").map((position: any) => (
                                <SelectItem key={position.id} value={position.id}>
                                  {position.displayName || position.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </CornerLabelField>
                      </div>
                    </div>

                    {/* Role */}
                    <div>
                      <CornerLabelField label={t('pages.contractors:form.fields.role')}>
                        <Select
                          value={userForm.role || 'contractor'}
                          onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('pages.contractors:form.placeholders.role')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contractor">{t('pages.contractors:form.options.role.contractor')}</SelectItem>
                            <SelectItem value="watcher">{t('pages.contractors:form.options.role.watcher')}</SelectItem>
                            <SelectItem value="business_owner">{t('pages.contractors:form.options.role.businessOwner')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </CornerLabelField>
                    </div>
                  
                    {/* Mobile Phone */}
                    <div>
                      <CornerLabelField label={`${t('pages.contractors:form.fields.mobile')} ${!isRegisteredUser ? '*' : ''}`}>
                          <PhoneInput
                          id="add-mobile"
                          value={userForm.mobile || ""}
                          onChange={(value) => setUserForm(prev => ({ ...prev, mobile: value }))}
                          defaultCountry={defaultCountry}
                          allowInvalid
                          disabled={isRegisteredUser}
                          error={userForm.mobile && !isValidPhoneNumber(userForm.mobile) ? t('pages.contractors:form.validation.mobile.invalid') : undefined}
                        />
                      </CornerLabelField>
                    </div>

                    {/* E-Mail */}
                    <div>
                      <CornerLabelField label={`${t('pages.contractors:form.fields.email')} ${!isRegisteredUser ? '*' : ''}`}>
                        <Input
                          id="add-email"
                          type="email"
                          value={userForm.email || ""}
                          onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder={t('pages.contractors:form.placeholders.email') || 'email@example.com'}
                          disabled={isRegisteredUser}
                          className={isRegisteredUser ? 'bg-gray-50 text-[#666666] cursor-not-allowed' : undefined}
                        />
                      </CornerLabelField>
                    </div>

                    {/* Projects - Checkboxes */}
                    <div>
                      <CornerLabelField
                        label={t('pages.contractors:form.fields.project')}
                        labelBgClassName="bg-[#DDDDDD]"
                      >
                        <div
                          className="space-y-2 max-h-40 overflow-y-auto border border-black rounded-[8px] p-2 mt-1"
                          style={{ borderWidth: '0.5px' }}
                        >
                          {projects.filter((project: any) => project.name && project.name.trim() !== "").map((project: any) => (
                            <div key={project.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`add-project-${project.id}`}
                                className="corner-checkbox"
                                checked={selectedProjectIds.includes(project.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedProjectIds([...selectedProjectIds, project.id]);
                                    if (selectedProjectIds.length === 0) {
                                      setSelectedProjectImage(project.imageUrl || "");
                                    }
                                  } else {
                                    const newIds = selectedProjectIds.filter((id: string) => id !== project.id);
                                    setSelectedProjectIds(newIds);
                                    if (newIds.length === 0) {
                                      setSelectedProjectImage("");
                                    } else {
                                      const firstProject = projects.find((p: any) => p.id === newIds[0]);
                                      setSelectedProjectImage(firstProject?.imageUrl || "");
                                    }
                                  }
                                }}
                              />
                              <label
                                htmlFor={`add-project-${project.id}`}
                                className="text-xs font-normal text-slate-900 cursor-pointer flex-1"
                              >
                                {project.name}
                              </label>
                            </div>
                          ))}
                          {projects.filter((project: any) => project.name && project.name.trim() !== "").length === 0 && (
                            <div className="text-xs text-slate-500 text-center py-2">
                              {t('pages.contractors:form.options.project.none')}
                            </div>
                          )}
                        </div>
                      </CornerLabelField>
                    </div>
                  </div>

                  {/* Right Column - Profile Photo / Project Image and Vacation */}
                  <div className="space-y-4">
                    {/* Profile photo for registered user (ConicGradientBorder, no container), otherwise project image */}
                    <div>
                      {isRegisteredUser ? (
                        <div className="flex justify-center">
                          <ConicGradientBorder size={80} borderWidth={4}>
                            {editingUser?.profilePhotoUrl ? (
                              <img
                                src={editingUser.profilePhotoUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-600 text-lg font-semibold">
                                {(userForm.name?.charAt(0) || '').toUpperCase()}{(userForm.lastName?.charAt(0) || '').toUpperCase() || '?'}
                              </div>
                            )}
                          </ConicGradientBorder>
                        </div>
                      ) : (
                        <div className="w-full h-40 border border-gray-400 flex items-center justify-center bg-slate-50 overflow-hidden">
                          {selectedProjectImage ? (
                            <img
                              src={selectedProjectImage}
                              alt="Projektbild"
                              className="w-full h-full object-cover"
                              onError={() => setSelectedProjectImage("")}
                            />
                          ) : (
                            <div className="text-center">
                              <div className="w-8 h-8 mx-auto mb-2 text-slate-400">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                                  <polyline points="21,15 16,10 5,21" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              </div>
                              <p className="text-xs text-slate-500 font-medium">Bild</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Vacation Section */}
                    <div>
                      <CornerLabelField
                        label={t('pages.contractors:form.fields.vacation')}
                        labelBgClassName="bg-[#DDDDDD]"
                      >
                        <div
                          className="space-y-2 mt-1 border border-black rounded-[8px] p-2"
                          style={{ borderWidth: '0.5px' }}
                        >
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <label className="text-xs text-slate-600">
                                {t('pages.contractors:form.fields.startDate')}
                              </label>
                              <Input
                                type="date"
                                value={userForm.vacationStart || ""}
                                onChange={(e) => setUserForm(prev => ({ ...prev, vacationStart: e.target.value }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs text-slate-600">
                                {t('pages.contractors:form.fields.endDate')}
                              </label>
                              <Input
                                type="date"
                                value={userForm.vacationEnd || ""}
                                onChange={(e) => setUserForm(prev => ({ ...prev, vacationEnd: e.target.value }))}
                              />
                            </div>
                          </div>
                          
                          {/* Display vacation period if both dates are set */}
                          {userForm.vacationStart && userForm.vacationEnd && (
                            <div className="mt-2 p-2 bg-slate-50 rounded border">
                              <p className="text-xs text-slate-600">
                                Zeitraum: {formatDate(userForm.vacationStart)} - {formatDate(userForm.vacationEnd)}
                              </p>
                            </div>
                          )}
                        </div>
                      </CornerLabelField>
                    </div>
                  </div>
                </div>

                {/* Notes - Full Width */}
                <div className="mt-4">
                  <CornerLabelField label={`${t('pages.contractors:form.fields.notes')} (${userForm.notes.length}/160)`}>
                    <Textarea
                      id="add-notes"
                      value={userForm.notes || ""}
                      onChange={(e) => {
                        if (e.target.value.length <= 160) {
                          setUserForm(prev => ({ ...prev, notes: e.target.value }));
                        }
                      }}
                      placeholder={t('pages.contractors:form.placeholders.notes') || 'Additional information...'}
                      className="corner-label-field-textarea-multiline"
                    />
                  </CornerLabelField>
                </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
    </AppShell>
  );
};

export default Contractors;
